// Fetch-обёртка для публичного API лендинга.
// Префикс, /api/public (Vite dev-proxy в vite.config.ts).
// В prod VITE_API_URL указывает на https://api.resstro.ru.
// Берёт access-токен из auth.ts и прозрачно обновляет его при 401.

import {
  getAccessToken,
  getRefreshToken,
  updateAccessToken,
  logout,
} from './auth';

const API_ORIGIN = (import.meta.env.VITE_API_URL?.trim() || '').replace(/\/+$/, '');
const BASE = `${API_ORIGIN}/api/public`;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type BodyInit = Record<string, unknown> | FormData | null | undefined;

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: BodyInit;
  auth?: boolean; // default: true, если есть токен; публичные signup/login явно ставят false
  signal?: AbortSignal;
}

// Русский фоллбек, если бэкенд почему-то не прислал текст ошибки (вместо «HTTP 402»).
function ruStatus(status: number): string {
  switch (status) {
    case 400: return 'Некорректный запрос';
    case 401: return 'Требуется вход в систему';
    case 402: return 'Эта возможность доступна на платном тарифе';
    case 403: return 'Доступ запрещён';
    case 404: return 'Не найдено';
    case 409: return 'Данные конфликтуют с уже существующими';
    case 429: return 'Слишком много запросов. Попробуйте через минуту';
    case 503: return 'Сервис временно недоступен. Попробуйте позже';
    default: return status >= 500 ? 'Внутренняя ошибка сервера' : 'Не удалось выполнить запрос';
  }
}

async function parseError(res: Response): Promise<ApiError> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      const data = (await res.json()) as { error?: string; code?: string; details?: unknown };
      return new ApiError(
        res.status,
        data.error || ruStatus(res.status),
        data.code,
        data.details,
      );
    } catch {
      /* fall-through */
    }
  }
  const text = await res.text().catch(() => '');
  return new ApiError(res.status, text || ruStatus(res.status));
}

async function doRequest(
  path: string,
  opts: Options,
  token: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  let body: string | FormData | undefined;

  if (opts.body != null) {
    if (opts.body instanceof FormData) {
      body = opts.body;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
  }
  if (token) headers.Authorization = `Bearer ${token}`;

  return fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body,
    signal: opts.signal,
  });
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshOnce(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refresh = getRefreshToken();
    if (!refresh) return null;
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        access_token: string;
        refresh_token: string;
      };
      updateAccessToken(data.access_token, data.refresh_token);
      return data.access_token;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const wantAuth = opts.auth !== false;
  let token = wantAuth ? getAccessToken() : null;

  let res = await doRequest(path, opts, token);

  if (res.status === 401 && wantAuth && getRefreshToken()) {
    const newToken = await refreshOnce();
    if (newToken) {
      token = newToken;
      res = await doRequest(path, opts, token);
    }
    if (res.status === 401) {
      logout();
      throw await parseError(res);
    }
  }

  if (!res.ok) throw await parseError(res);

  if (res.status === 204) return undefined as unknown as T;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

export const apiGet = <T>(path: string, opts?: Omit<Options, 'method' | 'body'>) =>
  api<T>(path, { ...opts, method: 'GET' });
export const apiPost = <T>(path: string, body?: BodyInit, opts?: Omit<Options, 'method'>) =>
  api<T>(path, { ...opts, method: 'POST', body });
export const apiPatch = <T>(path: string, body?: BodyInit, opts?: Omit<Options, 'method'>) =>
  api<T>(path, { ...opts, method: 'PATCH', body });
